import { CardSelectorOption } from '../components/controllers/card-selector/card-selector.interface';
import {
  ShippingOption,
  ShippingOptionSettings
} from '../../modules/checkout/models/delivery-methods';

/**
 * Converts an array of ShippingOptions to CardSelectorOptions.
 * When cart context is provided, methods whose settings requirements
 * are not met will be marked disabled with an explanatory badge.
 */
export function mapShippingOptionsToCardOptions(
  shippingOptions: ShippingOption[],
  cartSubtotal?: number,
  cartItemCount?: number
): CardSelectorOption<ShippingOption>[] {
  return shippingOptions.map((shippingOption) => {
    const { disabled, badge } = evaluateMethodEligibility(
      shippingOption.settings,
      cartSubtotal,
      cartItemCount
    );

    return {
      id: shippingOption.id,
      label: shippingOption.name,
      description: shippingOption.description,
      icon: shippingOption.icon,
      price: shippingOption.price,
      data: shippingOption,
      disabled,
      badge
    };
  });
}

/**
 * Returns the first shipping option that meets its settings requirements,
 * or the first option if none have settings, or undefined if list is empty.
 */
export function findFirstEligibleMethod(
  methods: ShippingOption[],
  cartSubtotal?: number,
  cartItemCount?: number
): ShippingOption | undefined {
  if (!methods.length) return undefined;

  const eligible = methods.find((m) => {
    const { disabled } = evaluateMethodEligibility(
      m.settings,
      cartSubtotal,
      cartItemCount
    );
    return !disabled;
  });

  return eligible ?? methods[0];
}

function evaluateMethodEligibility(
  settings: ShippingOptionSettings | undefined,
  cartSubtotal?: number,
  cartItemCount?: number
): { disabled: boolean; badge?: string } {
  if (!settings || cartSubtotal == null) {
    return { disabled: false };
  }

  if (
    settings.minimum_order_amount != null &&
    cartSubtotal < settings.minimum_order_amount
  ) {
    return {
      disabled: true,
      badge: `Min. $${settings.minimum_order_amount} order`
    };
  }

  if (
    settings.maximum_order_amount != null &&
    cartSubtotal > settings.maximum_order_amount
  ) {
    return {
      disabled: true,
      badge: `Max. $${settings.maximum_order_amount} order`
    };
  }

  if (
    settings.cart_base_min_enabled &&
    settings.cart_base_min_products != null &&
    cartItemCount != null &&
    cartItemCount < settings.cart_base_min_products
  ) {
    return {
      disabled: true,
      badge: `Min. ${settings.cart_base_min_products} items`
    };
  }

  return { disabled: false };
}
